"use client";

import { useState } from "react";
import { Loader2Icon } from "lucide-react";
import CustomEditorProvider from "@/providers/custom-editor-provider";
import ProductCommentInputField from "./product-comment-input-field";
import ProductCommentCard from "./product-comment-card";

type TReplyToComment = {
	show: boolean;
	commentId?: string;
};

const ProductComments = ({
	productSlug,
	comments = [],
	isError,
	fetchNextPage,
	hasNextPage,
	isFetchingNextPage,
}: {
	productSlug: string;
	comments?: any[];
	isError: boolean;
	fetchNextPage: () => void;
	hasNextPage?: boolean;
	isFetchingNextPage: boolean;
}) => {
	const [replyToComment, setReplyToComment] = useState<TReplyToComment>();

	const toggleReply = (commentId?: string) =>
		setReplyToComment((prev) =>
			prev?.show && prev.commentId === commentId ? undefined : { show: true, commentId }
		);

	return (
		<CustomEditorProvider defaultValue={{ editorType: "comment" }}>
			<ProductCommentInputField productSlug={productSlug} />

			<div className="mt-6 grid gap-4">
				{isError ? (
					<p className="text-sm text-red-500">Unable to load comments. Please try again later.</p>
				) : comments.length === 0 ? (
					<p className="text-sm text-gray-500">
						No comments yet. Be the first to share your thoughts!
					</p>
				) : (
					comments.map((comment) => (
						<ProductCommentCard
							key={comment.id}
							comment={comment}
							productSlug={productSlug}
							showReplyInput={replyToComment?.show}
							selectedCommentId={replyToComment?.commentId}
							isReply={false}
							toggleReply={toggleReply}
							onReplySuccess={() => setReplyToComment(undefined)}
						/>
					))
				)}
			</div>

			<div className="mt-6 text-center">
				{hasNextPage && (
					<button
						onClick={() => fetchNextPage()}
						disabled={isFetchingNextPage}
						className="mx-auto block rounded-lg bg-white px-6 py-3 text-sm font-medium text-gray-900 disabled:opacity-40"
					>
						{isFetchingNextPage ? (
							<Loader2Icon className="h-5 w-5 animate-spin stroke-brand-primary" />
						) : (
							"Show more"
						)}
					</button>
				)}
			</div>
		</CustomEditorProvider>
	);
};

export default ProductComments;
